import type {JSONSchema7} from 'json-schema';
import {round} from 'lodash';
import {StringUtils} from '../../utils/string.util';
import {JsonSchemaUtils} from '../../utils/json-schema.utils';

export class JsonSchemaFormContext {
    public readonly resolvedSchemaCache = new WeakMap<JSONSchema7, JSONSchema7>();
    public readonly parsedSubPropertiesCache = new WeakMap<JSONSchema7, JsonSchemaFormParseResult>();
    public readonly validationErrors: Record<string, string> = {};
    public readonly rootSchema: JSONSchema7;
    public readonly rowReferences: JsonSchemaRowReference[] = [];
    public readonly rowReferencesOptions: Record<string, {key: string | number; label: string}[]> = {};
    public rootData: unknown;

    /**
     * Creates a form context for the supplied root schema.
     * @param rootSchema Root schema used to resolve schema references.
     */
    public constructor(rootSchema: JSONSchema7) {
        this.rootSchema = rootSchema;
    }
}

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type JsonSchemaFormProperty = {
    id: string;
    label: string;
    schema: JSONSchema7;
    metas: JsonSchemaFormMeta;
    types: string[];
    initialType: string;
    arrayItemType: JSONSchema7 | null;
    arrayItemProperty: JsonSchemaFormProperty | null;
    required: boolean;
    nullable: boolean;
    order: number;
    isSecret: boolean;
    readOnly: boolean;
    writeOnly: boolean;

    sub: JsonSchemaFormParseResult | null;
    tableSub: JsonSchemaFormProperty[] | null;
    rowReference: JsonSchemaRowReference | null;
};

export interface JsonSchemaRowReference {
    key: string;
    pathFromRoot: string;
    attributeName: string;
    labelExpression: string;
}

export function getRowReferenceOptionsKey(rowReference: JsonSchemaRowReference): string {
    return [rowReference.pathFromRoot, rowReference.attributeName, rowReference.labelExpression].join('\u0000');
}

export interface JsonSchemaFormMeta {
    uiCol: number;
    tab: string;
    noParentFrame: boolean;
    frameDefaultCollapsed: boolean;
    allowHtml: boolean;
}

export interface JsonSchemaFormParseResultColumn {
    index: number;
    style: string;
    properties: JsonSchemaFormProperty[];
}

export interface JsonSchemaFormParseResult {
    properties: JsonSchemaFormProperty[];
    columns: Record<string, JsonSchemaFormParseResultColumn[]>;
    tabs: string[];
}

export const DEFAULT_TAB_NAME = 'Default';

export const DEFAULT_JSON_SCHEMA_FORM_META: JsonSchemaFormMeta = {
    uiCol: 0,
    tab: DEFAULT_TAB_NAME,
    noParentFrame: false,
    frameDefaultCollapsed: false,
    allowHtml: false
};

export abstract class JsonSchemaFormUtils {
    /**
     * Parses a schema into form properties, tabs, and layout columns.
     * @param ctx Form context containing schema caches and references.
     * @param from Schema to parse.
     * @returns Parsed form properties and layout information.
     */
    public static parseSchema(ctx: JsonSchemaFormContext, from: JSONSchema7): JsonSchemaFormParseResult {
        const cached = ctx.parsedSubPropertiesCache.get(from);
        if (cached) {
            return cached;
        }

        const properties: JsonSchemaFormProperty[] = [];
        const columnCount: Record<string, number> = {};
        const tabs: string[] = [];

        const lookupSchema = JsonSchemaFormUtils.resolveSchema(ctx, from);
        JsonSchemaFormUtils.parseSchemaProperties(ctx, lookupSchema, columnCount, tabs, properties);

        const parsed: JsonSchemaFormParseResult = {
            properties: properties,
            columns: {},
            tabs: tabs
        };
        tabs.forEach((tab) => {
            const columns: JsonSchemaFormParseResultColumn[] = [];
            const columnStyle = 'width: ' + round(100 / columnCount[tab], 4).toString() + '% !important';

            for (let i = 0; i < columnCount[tab]; ++i) {
                columns.push({
                    index: i,
                    style: columnStyle,
                    properties: properties.filter((prop) => prop.metas.uiCol == i && prop.metas.tab == tab)
                });
            }

            parsed.columns[tab] = columns;
        });

        ctx.parsedSubPropertiesCache.set(lookupSchema, parsed);

        return parsed;
    }

    /**
     * Resolves references and nullable unions while caching the result.
     * @param ctx Form context containing the root schema and resolution cache.
     * @param schema Schema to resolve.
     * @returns Resolved schema.
     */
    public static resolveSchema(ctx: JsonSchemaFormContext, schema: JSONSchema7): JSONSchema7 {
        const cached = ctx.resolvedSchemaCache.get(schema);
        if (cached) {
            return cached;
        }

        let resolvedSchema = schema;
        resolvedSchema = JsonSchemaFormUtils.extractPotentialAnyOf(resolvedSchema);

        if (resolvedSchema.$ref !== undefined) {
            const rowRef = JsonSchemaUtils.parseRowReference(resolvedSchema.$ref, ctx.rootSchema);
            if (rowRef.isRowReference) {
                const newSchema = {
                    ...schema
                };

                if (newSchema.$ref) {
                    delete newSchema.$ref;
                }

                const rowReference: JsonSchemaRowReference = {
                    key: '',
                    pathFromRoot: (newSchema as Record<string, string>)['x-reference-path-from-root'],
                    attributeName: rowRef.propertyName!,
                    labelExpression: rowRef.referenceExpression!
                };
                rowReference.key = getRowReferenceOptionsKey(rowReference);

                newSchema.type = 'string';
                (newSchema as Record<string, JsonSchemaRowReference>)['x-row-reference'] = rowReference;
                delete (newSchema as Record<string, string>)['x-reference-path-from-root'];

                resolvedSchema = newSchema;

                if (
                    !ctx.rowReferences.some(
                        (x: JsonSchemaRowReference) =>
                            x.pathFromRoot === rowReference.pathFromRoot &&
                            x.attributeName === rowReference.attributeName &&
                            x.labelExpression === rowReference.labelExpression
                    )
                ) {
                    ctx.rowReferences.push(rowReference);
                }
            } else {
                const resolved = JsonSchemaUtils.resolveRef(resolvedSchema.$ref, ctx.rootSchema);
                if (!resolved) throw new Error(`Could not resolve JsonSchema ref '${resolvedSchema.$ref}'`);

                resolvedSchema = {
                    ...resolved,
                    ...resolvedSchema
                };

                delete resolvedSchema.$ref;
            }

            resolvedSchema = JsonSchemaFormUtils.extractPotentialAnyOf(resolvedSchema);
        }

        ctx.resolvedSchemaCache.set(schema, resolvedSchema);
        return resolvedSchema;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Rebuilds selectable options for every row reference from the current form data.
     * @param ctx Form context containing row references and current root data.
     * @returns Nothing. The options are updated in the supplied context.
     */
    public static refreshContextRowReferencesOptions(ctx: JsonSchemaFormContext) {
        Object.keys(ctx.rowReferencesOptions).forEach((key) => {
            delete ctx.rowReferencesOptions[key];
        });

        for (const rowReference of ctx.rowReferences) {
            const rows = JsonSchemaFormUtils.getValueAtPath(ctx.rootData, rowReference.pathFromRoot);
            if (!Array.isArray(rows)) {
                continue;
            }

            const options = (ctx.rowReferencesOptions[rowReference.key] ??= []);

            // Clear options
            options.length = 0;

            rows.forEach((row) => {
                if (row === null || typeof row !== 'object') return;

                const key = (row as Record<string, unknown>)[rowReference.attributeName];
                if (key === undefined || key === null) return;

                options.push({
                    key: key as string | number,
                    label: JsonSchemaFormUtils.interpolateRowReferenceLabel(row, rowReference.labelExpression)
                });
            });
        }
    }

    /**
     * Resolves a basic Jinja-style property path against an arbitrary value.
     *
     * Supported path segments can be chained in any combination:
     * `property`, `property.nested`, `items[0]`, `items['key']`, and
     * `items[0].nested['display name']`.
     *
     * This intentionally supports data access only; it does not evaluate
     * expressions, function calls, filters, or operators.
     * @param value Value from which to read.
     * @param path Jinja-style property path.
     * @returns Value at the path, or `undefined` when the path is invalid or cannot be read.
     */
    private static getValueAtPath(value: unknown, path: string): unknown {
        const parts = JsonSchemaFormUtils.parsePath(path);
        if (!parts) return undefined;

        return parts.reduce<unknown>((current, part) => {
            if (current === null || current === undefined || typeof current !== 'object') return undefined;
            return (current as Record<string, unknown>)[part];
        }, value);
    }

    /**
     * Tokenizes a basic Jinja-style data path into property and bracket segments.
     *
     * Examples:
     * - `name.display` becomes `['name', 'display']`.
     * - `items[0].label` becomes `['items', '0', 'label']`.
     * - `metadata['display name']` becomes `['metadata', 'display name']`.
     *
     * Dot and bracket access can be chained, with optional whitespace around
     * segments. Expressions that contain unsupported or unclosed syntax return
     * `null` instead of being evaluated.
     *
     * @param path Jinja-style property path to tokenize.
     * @returns Path segments, or `null` when the path is invalid or empty.
     */
    private static parsePath(path: string): string[] | null {
        const parts: string[] = [];
        let index = 0;

        const skipWhitespace = () => {
            while (index < path.length && /\s/.test(path[index])) index++;
        };

        const readIdentifier = (): string | null => {
            const start = index;
            while (index < path.length && path[index] !== '.' && path[index] !== '[' && !/\s/.test(path[index]))
                index++;
            return start === index ? null : path.slice(start, index);
        };

        const readBracketKey = (): string | null => {
            index++;
            skipWhitespace();
            if (index >= path.length) return null;

            const quote = path[index] === "'" || path[index] === '"' ? path[index++] : null;
            const start = index;
            while (index < path.length && (quote ? path[index] !== quote : path[index] !== ']')) index++;
            if (index >= path.length) return null;

            const key = path.slice(start, index);
            if (quote) {
                index++;
                skipWhitespace();
                if (path[index] !== ']') return null;
            }

            index++;
            return key;
        };

        skipWhitespace();
        if (index < path.length && path[index] !== '[') {
            const identifier = readIdentifier();
            if (!identifier) return null;
            parts.push(identifier);
        }

        while (index < path.length) {
            skipWhitespace();
            if (index >= path.length) break;

            if (path[index] === '.') {
                index++;
                skipWhitespace();
                const identifier = readIdentifier();
                if (!identifier) return null;
                parts.push(identifier);
            } else if (path[index] === '[') {
                const key = readBracketKey();
                if (key === null) return null;
                parts.push(key);
            } else {
                return null;
            }
        }

        return parts.length > 0 ? parts : null;
    }

    /**
     * Replaces row-label placeholders with values from the current row.
     *
     * For example, `{{ name.display }} - {{ aliases[0] }}` resolves both
     * chained property and array access segments. Missing or invalid
     * placeholders resolve to an empty string.
     * @param row Row object used as the expression context.
     * @param expression Label expression containing optional Mustache placeholders.
     * @returns Label with placeholders replaced by row values.
     */
    private static interpolateRowReferenceLabel(row: object, expression: string): string {
        return expression.replace(/\{\{[^{}]*\}\}/g, (match) => {
            const value = JsonSchemaFormUtils.getValueAtPath(row, match.slice(2, -2).trim());
            return value === undefined || value === null ? '' : JsonSchemaFormUtils.toDisplayString(value);
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * Converts schema properties into form property metadata and layout information.
     * @param ctx Form context containing schema caches and references.
     * @param from Schema whose properties should be converted.
     * @param columnCount Mutable column counts grouped by tab.
     * @param tabs Mutable list of discovered tab names.
     * @param properties Mutable list of parsed form properties.
     * @returns Nothing. Parsed values are written to the supplied collections.
     */
    private static parseSchemaProperties(
        ctx: JsonSchemaFormContext,
        from: JSONSchema7,
        columnCount: Record<string, number>,
        tabs: string[],
        properties: JsonSchemaFormProperty[]
    ) {
        if (!from.properties) return;

        Object.keys(from.properties).forEach((propertyId) => {
            let propertySchema = from.properties![propertyId] as JSONSchema7;
            propertySchema = JsonSchemaFormUtils.resolveSchema(ctx, propertySchema);

            const required = from.required?.includes(propertyId) ?? false;
            const typeList = JsonSchemaFormUtils.getSchemaTypes(propertySchema.type);

            const metas = JsonSchemaFormUtils.resolvePropertyMetas(propertySchema);
            const order = ((propertySchema as Record<string, unknown>)?.['x-order'] as number | undefined) || 0;
            const isSecret = (propertySchema as Record<string, unknown>)['x-is-secret'] === true;
            const sub = propertySchema.properties ? JsonSchemaFormUtils.parseSchema(ctx, propertySchema) : null;
            const rowReference =
                ((propertySchema as Record<string, JsonSchemaRowReference>)['x-row-reference'] as
                    | JsonSchemaRowReference
                    | undefined) || null;

            let arrayItemType: JSONSchema7 | null = null;
            let arrayItemProperty: JsonSchemaFormProperty | null = null;
            let tableSub: JsonSchemaFormProperty[] | null = null;

            if (typeList.includes('array')) {
                if (Array.isArray(propertySchema.items) || propertySchema.items === true) {
                    throw new Error('Unsupported multiple type array');
                }
                arrayItemType = propertySchema.items as JSONSchema7;
                arrayItemType = JsonSchemaFormUtils.resolveSchema(ctx, arrayItemType);

                const arrayItemTypeList = JsonSchemaFormUtils.getSchemaTypes(arrayItemType.type);
                const arrayItemSub =
                    arrayItemType.type === 'object' ? JsonSchemaFormUtils.parseSchema(ctx, arrayItemType) : null;
                const arrayItemRowReference =
                    ((arrayItemType as Record<string, JsonSchemaRowReference>)['x-row-reference'] as
                        | JsonSchemaRowReference
                        | undefined) || null;

                arrayItemProperty = {
                    id: 'Value',
                    label: StringUtils.toHumanFormatedStr('Value'),
                    schema: arrayItemType,
                    metas: {...DEFAULT_JSON_SCHEMA_FORM_META},
                    types: arrayItemTypeList,
                    initialType: arrayItemTypeList.find((x) => x != 'null')!,
                    arrayItemType: null,
                    arrayItemProperty: null,
                    required: true,
                    nullable: false,
                    order: 0,
                    isSecret: false,
                    readOnly: false,
                    writeOnly: false,

                    sub: arrayItemSub,
                    tableSub: null,
                    rowReference: arrayItemRowReference
                };

                if (propertySchema.format === 'table' && arrayItemType?.type === 'object') {
                    tableSub = JsonSchemaFormUtils.parseSchema(ctx, arrayItemType).properties;
                }
            }

            properties.push({
                id: propertyId,
                label: StringUtils.toHumanFormatedStr(propertyId),
                schema: propertySchema,
                metas: metas,
                types: typeList,
                required: required,
                initialType: typeList.find((x) => x != 'null')!,
                arrayItemType: arrayItemType,
                arrayItemProperty: arrayItemProperty,
                nullable: typeList.includes('null') || !required,
                order: order,
                isSecret: isSecret,
                readOnly: propertySchema.readOnly ?? false,
                writeOnly: propertySchema.writeOnly ?? false,

                sub: sub,
                tableSub: tableSub,
                rowReference: rowReference
            });

            // Array of a plain data type inherit array attributes
            if (arrayItemProperty && arrayItemProperty.initialType !== 'object') {
                const pushedProperty = properties[properties.length - 1];

                arrayItemProperty.isSecret = pushedProperty.isSecret;
                arrayItemProperty.readOnly = pushedProperty.readOnly;
                arrayItemProperty.writeOnly = pushedProperty.writeOnly;
            }

            if (!columnCount[metas.tab] || metas.uiCol + 1 >= columnCount[metas.tab])
                columnCount[metas.tab] = metas.uiCol + 1;
            if (!tabs.includes(metas.tab)) tabs.push(metas.tab);
        });

        properties.sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
            else if (a.order !== undefined) return -1;
            else if (b.order !== undefined) return 1;

            return a.id < b.id ? -1 : 1;
        });
    }
    /**
     * Normalizes the JSON Schema type to an array of strings.
     * @param type The JSON Schema type or array of types to normalize.
     * @returns An array of string types, ensuring at least one type is present.
     */
    private static getSchemaTypes(type: JSONSchema7['type']): string[] {
        if (type === undefined) return ['string'];
        return Array.isArray(type) ? type : [type];
    }
    /**
     * Merges custom layout metadata with the default form metadata.
     * @param propertySchema Schema containing optional `_meta` layout metadata.
     * @returns Complete form metadata for the property.
     */
    private static resolvePropertyMetas(propertySchema: JSONSchema7): JsonSchemaFormMeta {
        const srcMetas = (propertySchema as Record<string, unknown>)['_meta'] as JsonSchemaFormMeta;
        if (!srcMetas) return DEFAULT_JSON_SCHEMA_FORM_META;

        const result = {...DEFAULT_JSON_SCHEMA_FORM_META};

        if ('uiCol' in srcMetas) result.uiCol = srcMetas['uiCol'] as number;
        if ('tab' in srcMetas) result.tab = srcMetas['tab'] as string;
        if ('noParentFrame' in srcMetas) result.noParentFrame = srcMetas['noParentFrame'] as boolean;
        if ('frameDefaultCollapsed' in srcMetas)
            result.frameDefaultCollapsed = srcMetas['frameDefaultCollapsed'] as boolean;
        if ('allowHtml' in srcMetas) result.allowHtml = srcMetas['allowHtml'] as boolean;

        return result;
    }

    /**
     * Extracts the non-null schema from a nullable two-branch anyOf definition.
     * @param resolvedSchema Schema that may contain a nullable `anyOf`.
     * @returns Schema with the nullable union flattened when applicable.
     */
    private static extractPotentialAnyOf(resolvedSchema: JSONSchema7) {
        if (resolvedSchema.anyOf?.length == 2 && resolvedSchema.anyOf.some((x) => (x as JSONSchema7).type == 'null')) {
            resolvedSchema = {
                ...resolvedSchema,
                ...(resolvedSchema.anyOf.find((x) => (x as JSONSchema7).type != 'null') as JSONSchema7),
                anyOf: undefined
            };

            delete resolvedSchema.anyOf;
        }

        return resolvedSchema;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Prepare data for fields that may not be rendered immediately in large virtualized forms.
     * @param property Property metadata and schema used to prepare the value.
     * @param target Object containing the value to prepare.
     * @param targetKey Property key within `target`.
     * @returns Nothing. The target value is prepared in place.
     */
    public static prepareValueForSchema(
        property: JsonSchemaFormProperty,
        target: Record<string | number, unknown>,
        targetKey: string | number
    ): void {
        const propSchema = property.schema;

        if (target[targetKey] === null || target[targetKey] === undefined) {
            if (propSchema.default !== undefined && propSchema.default !== null) {
                target[targetKey] = JsonSchemaFormUtils.cloneSchemaDefaultValue(propSchema.default);
            } else if (property.required && property.initialType == 'object') {
                target[targetKey] = {};
            } else if (property.required && property.initialType == 'array') {
                target[targetKey] = [];
            }
        }

        if (
            property.initialType == 'string' &&
            propSchema.format == 'date' &&
            target[targetKey] &&
            typeof target[targetKey] == 'string'
        ) {
            target[targetKey] = new Date(target[targetKey] as string);
            return;
        }

        const currentValue = target[targetKey];
        if (property.initialType == 'object' && property.sub) {
            if (!JsonSchemaFormUtils.isRecord(currentValue)) {
                return;
            }

            property.sub.properties.forEach((subProp) => {
                JsonSchemaFormUtils.prepareValueForSchema(subProp, currentValue, subProp.id);
            });

            return;
        }

        if (
            property.initialType == 'array' &&
            property.arrayItemType &&
            property.arrayItemProperty &&
            Array.isArray(currentValue)
        ) {
            currentValue.forEach((_, index) => {
                JsonSchemaFormUtils.prepareValueForSchema(
                    property.arrayItemProperty!,
                    currentValue as Record<number, unknown>,
                    index
                );
            });
        }
    }
    /**
     * Validates a schema value without requiring all nested form controls to be rendered.
     * @param property Property metadata and schema used for validation.
     * @param value Value to validate.
     * @param path Formatted property path used in validation errors.
     * @param validationErrors Mutable map receiving validation errors.
     * @returns Nothing. Validation errors are written to `validationErrors`.
     */
    public static validateValueForSchema(
        property: JsonSchemaFormProperty,
        value: unknown,
        path: string,
        validationErrors: Record<string, string>
    ): void {
        const propSchema = property.schema;
        const selectedType = property.initialType;
        const isNulled = value === null || value === undefined;

        if (isNulled) {
            const isRequiredComputed = property.nullable ? !isNulled : property.required;
            if (isRequiredComputed) {
                validationErrors[path] = `${path}: Missing value`;
            }
            return;
        }

        if (selectedType == 'string') {
            const error = JsonSchemaFormUtils.getStringValidationError(
                propSchema,
                JsonSchemaFormUtils.toDisplayString(value)
            );
            if (error) {
                validationErrors[path] = `${path}: ${error}`;
            }
            return;
        }

        if (selectedType == 'object' && property.sub) {
            if (!JsonSchemaFormUtils.isRecord(value)) {
                return;
            }

            property.sub.properties.forEach((subProp) => {
                JsonSchemaFormUtils.validateValueForSchema(
                    subProp,
                    value[subProp.id],
                    `${path}.${subProp.id}`,
                    validationErrors
                );
            });

            return;
        }

        if (
            property.initialType == 'array' &&
            property.arrayItemType &&
            property.arrayItemProperty &&
            Array.isArray(value)
        ) {
            value.forEach((item, index) => {
                JsonSchemaFormUtils.validateValueForSchema(
                    property.arrayItemProperty!,
                    item,
                    `${path}[${index}]`,
                    validationErrors
                );
            });
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Determines whether a value is a non-array object record.
     * @param value Value to inspect.
     * @returns `true` when the value is a non-null, non-array object record.
     */
    private static isRecord(value: unknown): value is Record<string | number, unknown> {
        return typeof value === 'object' && value !== null && !Array.isArray(value);
    }

    /**
     * Clones object and array defaults before assigning them to form data.
     * @param defaultValue Schema default value.
     * @returns Cloned object or array, or the original primitive value.
     */
    private static cloneSchemaDefaultValue(defaultValue: JSONSchema7['default']) {
        if (typeof defaultValue === 'object') {
            return structuredClone(defaultValue);
        }

        return defaultValue;
    }

    /**
     * Converts a value to a string suitable for display purposes.
     * @param value Value to convert to a display string.
     * @returns String representation suitable for display.
     */
    private static toDisplayString(value: unknown): string {
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') {
            try {
                return JSON.stringify(value) ?? '';
            } catch {
                return Object.prototype.toString.call(value);
            }
        }
        return String(value);
    }

    /**
     * Returns a validation message for a string against its schema constraints.
     * @param schema Schema containing string validation constraints.
     * @param value String value to validate.
     * @returns Validation message, or `undefined` when the value is valid.
     */
    private static getStringValidationError(schema: JSONSchema7, value: string): string | undefined {
        if (value.length == 0) {
            return 'Missing value';
        }

        if (schema.minLength !== undefined && value.length < schema.minLength) {
            return `Value must be at least ${schema.minLength} characters long`;
        }

        if (schema.maxLength !== undefined && value.length > schema.maxLength) {
            return `Value must be at most ${schema.maxLength} characters long`;
        }

        if (!schema.pattern) {
            return undefined;
        }

        try {
            if (!new RegExp(schema.pattern).test(value)) {
                return 'Invalid format';
            }

            return undefined;
        } catch {
            return 'Invalid format';
        }
    }
}
